import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap } from "lucide-react";
import { MaskedLine, Counter, scrollToId } from "./shared";

const STATS = [
  { value: 110000, suffix: "", label: "RPM Brushless Motor", testid: "stat-rpm" },
  { value: 33, suffix: " M/S", label: "Jet Stream Velocity", testid: "stat-wind" },
  { value: 8000, suffix: " mAh", label: "USB-C Battery", testid: "stat-battery" },
  { value: 420, suffix: " g", label: "Pocket Weight", testid: "stat-weight" },
];

export const Hero = ({ onBuy }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      {/* Parallax turbine backdrop with slow spin */}
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <motion.img
          src="/assets/pexels-turbine.jpg"
          alt=""
          initial={{ scale: 1.25, opacity: 0 }}
          animate={{ scale: 1.1, opacity: 0.5 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="h-full w-full object-cover"
        />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 140, repeat: Infinity, ease: "linear" }}
          className="absolute -right-[20%] top-1/2 hidden w-[70vw] -translate-y-1/2 opacity-25 lg:block"
        >
          <img src="/assets/pexels-turbine.jpg" alt="" className="w-full rounded-full" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
        <div className="spotlight absolute inset-0" />
      </motion.div>

      {/* Product spotlight card */}
      <motion.div
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.2, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-10 top-28 z-10 hidden w-[300px] xl:block"
      >
        <div className="group relative overflow-hidden border border-steel bg-carbon">
          <img
            src="/assets/cj-1.jpg"
            alt="X8 Violent Fan — the actual shipped unit in Iron Gray"
            className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-white/10 bg-ink/70 px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase backdrop-blur-md">
            <span className="text-smoke">Unit 001</span>
            <span className="text-volt">Ships From CJ</span>
          </div>
        </div>
      </motion.div>

      {/* Kinetic headline */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-1 flex-col justify-center px-5 pt-32 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-6 flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-smoke"
          data-testid="hero-overline"
        >
          <Zap className="h-4 w-4 text-volt" />
          The pocket turbine the internet calls violent
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.88] tracking-tighter">
          <MaskedLine delay={0.15} className="text-[15vw] text-volt md:text-[10.5vw]">
            110,000
          </MaskedLine>
          <MaskedLine delay={0.3} className="text-[11vw] md:text-[8vw]">
            RPM of pure
          </MaskedLine>
          <MaskedLine delay={0.45} className="text-[11vw] md:text-[8vw]">
            <span className="text-outline">Airborne</span> chaos
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-base text-smoke md:text-lg" data-testid="hero-subcopy">
            The X8 Violent Fan is a brushless jet engine for your pocket. It obliterates
            dust, dries cars, lights fires, and kills canned air forever.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-buy-button"
              onClick={onBuy}
              className="border border-volt bg-volt px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] uppercase text-ink transition-colors duration-200 hover:bg-transparent hover:text-volt"
            >
              Buy the X8 — $39.99
            </button>
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToId("#chapters")}
              className="group flex items-center gap-3 border border-steel px-8 py-4 font-mono text-sm tracking-[0.2em] uppercase text-foreground transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              See the power
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 mt-16 border-t border-steel bg-ink/60 backdrop-blur-md"
        data-testid="hero-stats-bar"
      >
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className={`px-6 py-6 md:px-10 ${i > 0 ? "border-l border-steel" : ""} ${i === 2 ? "max-lg:border-l-0 max-lg:border-t max-lg:border-steel" : ""} ${i === 3 ? "max-lg:border-t max-lg:border-steel" : ""}`}
            >
              <div className="font-mono text-2xl font-bold text-volt md:text-3xl">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
